import { useState, useEffect, useMemo } from "react";
import { getQueryString } from "utils/url";
import { isIOSDevice } from "utils/device";
import useArAvailable from "hooks/useArAvailable";

// styles
import { StyledTutorialContainer } from "./StyledTutorial";

// components
import QRcode from "components/QRcode/QRcode";
import ArStatusPopup from "components/ArStatusPopup/ArStatusPopup";

//img
import { ReactComponent as TouchIcon } from "./images/touch.svg";
import { ReactComponent as ArProjectionIcon } from "./images/pcAr.svg";
import { ReactComponent as SwitchHorizontal } from "./images/switch-horizontal.svg";
import close from "./images/close.svg";
import mouseScaleScroll from "./images/mouse-scale-scroll.svg";
import mouseRotateLeftdrag from "./images/mouse-rotate-leftdrag.svg";
import mouseMoveRightdrag from "./images/mouse-move-rightdrag.svg";
import mouseResetDoubleclick from "./images/mouse-reset-doubleclick.svg";
import handScalePinch from "./images/hand-scale-pinch.svg";
import handRotateOnefinger from "./images/hand-rotate-onefinger.svg";
import handMoveTwofinger from "./images/hand-move-twofinger.svg";
import handResetTap from "./images/hand-reset-tap.svg";

// i18n
import { useTranslation } from "react-i18next";
import { handleRerouteToARMaker } from "../../utils/redirectUrl";
import SERVER from "../../constants/server";

const Tutorial = (props) => {
  const { t } = useTranslation();

  const [showRemind, setShowRemind] = useState(false);
  const [fullBool, setFull] = useState(false);
  const isVisionPro = useMemo(() => getQueryString("device") === "vision-pro", []);

  const isGSModel = props.isGSModel || false;

  useEffect(() => {
    if (window.innerWidth > 1024) {
      if (fullBool === true) {
        if (document.documentElement.webkitRequestFullScreen) {
          document.documentElement.webkitRequestFullScreen();
        } else if (document.documentElement.mozRequestFullScreen) {
          document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.msRequestFullscreen) {
          document.documentElement.msRequestFullScreen();
        }
      } else {
        if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    }
  }, [fullBool]);

  const remindEvent = () => {
    if (props.loadingStatus) {
      return;
    }
    setShowRemind(!showRemind);
  };

  const fullEvent = () => {
    if (props.loadingStatus) {
      return;
    }
    setFull(!fullBool);
  };

  const [isQrcode, setQrcode] = useState(false);
  const [arStatus, setArStatus] = useState(null);

  // 與 AR 投放共用 canActivateAR 能力偵測：可投放顯示手指操作圖，否則顯示滑鼠操作圖
  const arAvailable = useArAvailable();
  const gestureImages = arAvailable
    ? {
        zoom: handScalePinch,
        rotate: handRotateOnefinger,
        smoothMove: handMoveTwofinger,
        default: handResetTap,
      }
    : {
        zoom: mouseScaleScroll,
        rotate: mouseRotateLeftdrag,
        smoothMove: mouseMoveRightdrag,
        default: mouseResetDoubleclick,
      };

  const switchToScankit = () => {
    const uuid = new URLSearchParams(window.location.search).get("uid");
    if (uuid) {
      window.open(`${SERVER.PLATFORM_URL}/viewer?uid=${uuid}`, "_self", "noopener,noreferrer");
    }
  };

  const handleArProjection = () => {
    if (props.loadingStatus) return;

    const getMovelViewer = document.querySelector("#myViewer");

    // 以 model-viewer 的 canActivateAR 能力偵測決定能否直接投放。
    // 它採 feature detection（webxr / scene-viewer / quick-look），
    // 平板即使切換「桌面版網站」模式也能正確判斷；不支援才顯示 QR code。
    if (!getMovelViewer?.canActivateAR) {
      // 電腦版裝置（無法投放）
      setQrcode(true);
      return;
    }

    setArStatus("偵測裝置中");

    // 判斷使用者是否使用IOS作業系統（含桌面模式 iPad）
    if (isIOSDevice()) {
      // 偵測瀏覽器
      let isChrome = navigator.userAgent.indexOf("CriOS") > -1;
      let isSafari = navigator.userAgent.indexOf("Safari") > -1;
      if (isChrome && isSafari) isSafari = false;

      // 判斷物件是否缺少USDZ檔案
      if (props.noUsdz) {
        // 缺少USDZ檔案，則無法開啟AR投放
        setArStatus("無法使用");
      } else if (isSafari) {
        // 判斷使用者是否使用Safari瀏覽器
        setArStatus("偵測裝置成功");
        getMovelViewer.activateAR();
      } else {
        // 使用非Safari瀏覽器，則無法開啟AR投放
        setArStatus("未使用Safari瀏覽器");
      }
    } else {
      // 其餘系統不需判斷檔案，直接開啟AR投放
      setArStatus("偵測裝置成功");
      getMovelViewer.activateAR();
    }
  };

  const [showSwitchButton, setShowSwitchButton] = useState(false);
  useEffect(() => {
    const hasScankitObject =
      props.productInfo?.some((product) => product.scanFileList.length > 0) ?? false;
    setShowSwitchButton(hasScankitObject);
  }, [props.productInfo]);

  return (
    <StyledTutorialContainer loadingStatus={props.loadingStatus} isAr={props.isAr}>
      {isQrcode && <QRcode setQrcode={setQrcode} modelIndex={props.modelIndex} />}
      {arStatus && <ArStatusPopup type={arStatus} modelIndex={props.modelIndex} />}
      <ul className="tutorialPanel">
        {!isGSModel && (
          <li onClick={remindEvent} className="flexCenter">
            <div className="blackRemind">
              <div className="blackRemind-text">{t("Tutorial.gestureAssistance")}</div>
              <div className="angle"></div>
            </div>
            <TouchIcon className="touchIcon" />
            {showRemind && (
              <div className="pop">
                <div className="top">
                  {t("Tutorial.gestureAssistance")}
                  <img src={close} onClick={remindEvent} alt="" />
                </div>
                <table>
                  <tbody>
                    <tr>
                      <td>{t("Tutorial.zoom")}</td>
                      <td>{t("Tutorial.rotate")}</td>
                      <td>{t("Tutorial.smoothMove")}</td>
                      <td>{t("Tutorial.default")}</td>
                    </tr>
                    <tr>
                      <td>
                        <img className="gestureIcon" src={gestureImages.zoom} alt="zoom" />
                      </td>
                      <td>
                        <img src={gestureImages.rotate} alt="rotate" />
                      </td>
                      <td>
                        <img src={gestureImages.smoothMove} alt="smoothMove" />
                      </td>
                      <td>
                        <img src={gestureImages.default} alt="default" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </li>
        )}
        {/* <li onClick={fullEvent}>
                    <div className="blackRemind">
                        <div className="blackRemind-text">{t('Tutorial.fullScreen')}</div>
                        <div className="angle"></div>
                    </div>
                    {
                        fullBool ? <FullClose /> : <FullOpen />
                    }
                </li> */}
        {!isVisionPro && !isGSModel && (
          <li onClick={handleArProjection}>
            <div className="blackRemind">
              <div className="blackRemind-text">{t("Tutorial.ARProjection")}</div>
              <div className="angle"></div>
            </div>
            <ArProjectionIcon style={{ display: "block" }} />
          </li>
        )}
        {showSwitchButton && (
          <li onClick={switchToScankit}>
            <div className="blackRemind">
              <div className="blackRemind-text">Switch To Scankit</div>
              <div className="angle"></div>
            </div>
            <SwitchHorizontal />
          </li>
        )}
      </ul>
    </StyledTutorialContainer>
  );
};

export default Tutorial;
